import { zodResolver } from '@hookform/resolvers/zod';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { useId, useState, type ComponentType, type ReactNode } from 'react';
import { FormProvider, useForm, type DefaultValues, type FieldValues, type Path, type UseFormReturn } from 'react-hook-form';

import { notifyApiError, useNotifier } from '@/shared/data';
import { FieldGrid, leafPaths } from '@/shared/form';
import { ApiError, type ErrorFeedback, type FieldError } from '@/shared/http';
import { useIsMobile } from '@/shared/mobile';
import { resolvePatternText } from '@/shared/patterns';
import { Alert } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { cn } from '@/shared/ui/cn';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, type DialogSize } from '@/shared/ui/dialog';
import { Drawer, DrawerBody, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle } from '@/shared/ui/drawer';
import { toast } from '@/shared/ui/toaster';

import { ActionStepProvider, useActionStepHost } from './action-step';
import { actionDialogSize, type ActionDefinition } from './define-action';
import { stepUpOf, type StepUpSubmit } from './step-up';
import { ReviewRows, TransactionSheet, type TransactionStep } from './transaction-sheet';

/** Desfazer fica 5 s no toast (U39 §6). */
export const UNDO_MS = 5000;

export interface ActionDialogProps<C, V extends FieldValues, R> {
  readonly action: ActionDefinition<C, V, R>;
  /** Um registro. */
  readonly ctx?: C;
  /** Lote: o `submit` roda uma vez por item, em sequência, com progresso (02 §11.1). */
  readonly batch?: readonly C[];
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** Depois do envio, com os resultados que deram certo (também no lote parcial). */
  readonly onDone?: (results: readonly R[]) => void;
}

interface Failure {
  readonly feedback: ErrorFeedback;
  readonly unmatched: readonly FieldError[];
}

interface BatchState {
  readonly done: number;
  readonly total: number;
  readonly failed: readonly { readonly record: string; readonly message: string }[];
  readonly finished: boolean;
}

/**
 * Erros de campo do `INVALID_FORM` nos campos do diálogo, pela regra do legado (`nome` contém a
 * `chave`). Devolve os que não casaram, para o aviso do topo.
 */
function applyFieldErrors<V extends FieldValues>(form: UseFormReturn<V>, errors: readonly FieldError[]): FieldError[] {
  const values = form.getValues() as Record<string, unknown>;
  const names = [...new Set([...Object.keys(values), ...leafPaths(values)])];
  const unmatched: FieldError[] = [];
  for (const error of errors) {
    const key = error.key.toLowerCase();
    const name = key ? names.find((candidate) => candidate.toLowerCase().includes(key)) : undefined;
    if (name && error.helpMessage) form.setError(name as Path<V>, { type: 'server', message: error.helpMessage });
    else unmatched.push(error);
  }
  return unmatched;
}

type Slot = ComponentType<{ readonly className?: string; readonly children: ReactNode }>;

interface Slots {
  readonly Header: Slot;
  readonly Title: Slot;
  readonly Description: Slot;
  readonly Body: Slot;
  readonly Footer: Slot;
}

const WEB: Slots = {
  Header: DialogHeader,
  Title: DialogTitle,
  Description: DialogDescription,
  Body: ({ className, children }) => <div className={cn('flex flex-col gap-4', className)}>{children}</div>,
  Footer: DialogFooter,
};

const MOBILE: Slots = {
  Header: DrawerHeader,
  Title: DrawerTitle,
  Description: DrawerDescription,
  Body: ({ className, children }) => <DrawerBody className={cn('flex flex-col gap-4', className)}>{children}</DrawerBody>,
  Footer: ({ className, children }) => <DrawerFooter className={cn('flex-row border-t border-border pt-3 [&>*]:flex-1', className)}>{children}</DrawerFooter>,
};

/**
 * Executor das ações com efeito da tela (X06, U39 §6): prévia legível, formulário curto,
 * confirmação com o verbo ("Sim, rejeitar"), erro da API por campo e sucesso com toast e Desfazer.
 * Web: diálogo central, `confirm` ou `form` (`actionDialogSize`); iPhone: bottom sheet ou tela cheia
 * (protótipo `m_approve`/`m_reject`).
 */
export function ActionDialog<C, V extends FieldValues, R>(props: ActionDialogProps<C, V, R>) {
  const stepUp = stepUpOf(props.action);
  if (stepUp) return <StepUpActionSheet {...props} stepUp={stepUp} />;
  return <PlainActionDialog {...props} />;
}

function PlainActionDialog<C, V extends FieldValues, R>(props: ActionDialogProps<C, V, R>) {
  const isMobile = useIsMobile();
  const [busy, setBusy] = useState(false);
  // Calculada só aberta (fechado, o contexto pode faltar) e mantida na saída, para não encolher animando.
  const openSize = props.open ? actionDialogSize(props.action, (props.batch ? props.batch[0] : props.ctx) as C) : undefined;
  const [size, setSize] = useState<DialogSize>(openSize ?? 'confirm');
  if (openSize && openSize !== size) setSize(openSize);
  const change = (next: boolean) => {
    if (!busy) props.onOpenChange(next);
  };
  const content = <ActionForm {...props} slots={isMobile ? MOBILE : WEB} busy={busy} setBusy={setBusy} />;
  if (isMobile) {
    const screen = props.action.mobile === 'screen';
    return (
      <Drawer open={props.open} onOpenChange={change} dismissible={!busy} repositionInputs={false}>
        <DrawerContent data-slot="action-dialog" className={cn(screen && 'h-[100dvh] max-h-[100dvh] rounded-none')}>
          {content}
        </DrawerContent>
      </Drawer>
    );
  }
  return (
    <Dialog open={props.open} onOpenChange={change}>
      <DialogContent data-slot="action-dialog" size={size} showClose={!busy} onInteractOutside={(event) => busy && event.preventDefault()}>
        {content}
      </DialogContent>
    </Dialog>
  );
}

interface ActionFormProps<C, V extends FieldValues, R> extends ActionDialogProps<C, V, R> {
  readonly slots: Slots;
  readonly busy: boolean;
  readonly setBusy: (busy: boolean) => void;
}

type Outcome<R> = { readonly single: true; readonly result: R } | { readonly single: false; readonly count: number };

/** O toast de sucesso da ação (com Desfazer quando há `undo`); lote: a contagem, sem Desfazer (o estorno é por registro). */
function useCelebrate<C, V extends FieldValues, R>(action: ActionDefinition<C, V, R>, first: C) {
  const { i18n, t } = useLingui();
  const notifier = useNotifier();
  const title = resolvePatternText(i18n, action.title(first));
  return (values: V, outcome: Outcome<R>) => {
    if (action.success === false) return;
    const message = outcome.single && action.success ? action.success(outcome.result, values, first) : undefined;
    const heading = message ? resolvePatternText(i18n, message.title) : outcome.single ? t`Feito: ${title}` : t`${outcome.count} concluídas: ${title}`;
    const description = message?.description ? resolvePatternText(i18n, message.description) : outcome.single ? action.record(first) : undefined;
    const undo = outcome.single ? action.undo : undefined;
    const result = outcome.single ? outcome.result : undefined;
    toast.success(heading, {
      ...(description ? { description } : {}),
      ...(undo
        ? {
            duration: UNDO_MS,
            action: {
              label: t`Desfazer`,
              onClick: () => {
                undo(result as R, values, first).catch((error: unknown) => {
                  notifyApiError(notifier, error);
                });
              },
            },
          }
        : {}),
    });
  };
}

function useActionForm<C, V extends FieldValues, R>(action: ActionDefinition<C, V, R>, first: C) {
  const { i18n } = useLingui();
  const schema = action.schema?.(first, i18n);
  return useForm<V, unknown, V>({
    ...(schema ? { resolver: zodResolver(schema) } : {}),
    defaultValues: (action.defaults?.(first) ?? {}) as DefaultValues<V>,
    mode: 'onTouched',
  });
}

function FailureAlert({ failure }: { readonly failure: Failure }) {
  const code = failure.feedback.code;
  const correlation = failure.feedback.correlationId;
  return (
    <Alert tone={failure.feedback.tone === 'warning' ? 'warn' : 'err'} title={failure.feedback.title}>
      <p>{failure.feedback.description}</p>
      {failure.unmatched.map((error) => (
        <p key={`${error.key}${error.helpMessage}`}>{error.helpMessage}</p>
      ))}
      {code || correlation ? <p className="mt-1 font-mono text-xs">{[code, correlation].filter(Boolean).join(' · ')}</p> : null}
    </Alert>
  );
}

function ActionForm<C, V extends FieldValues, R>({ action, ctx, batch, onOpenChange, onDone, slots, busy, setBusy }: ActionFormProps<C, V, R>) {
  const { i18n, t } = useLingui();
  const items: readonly C[] = batch ?? (ctx === undefined ? [] : [ctx]);
  const first = items[0] as C;
  const [failure, setFailure] = useState<Failure | undefined>(undefined);
  const [progress, setProgress] = useState<BatchState | undefined>(undefined);
  const form = useActionForm(action, first);

  const title = resolvePatternText(i18n, action.title(first));
  const summary = resolvePatternText(i18n, batch && action.batchSummary ? action.batchSummary(batch) : action.summary(first));
  const blocked = action.blocked?.(first);
  const verb = resolvePatternText(i18n, action.verb).toLocaleLowerCase(i18n.locale);
  const destructive = action.kind === 'destructive';
  const Icon = action.icon;
  const { Header, Title, Description, Body, Footer } = slots;
  const Fields = action.Fields;
  const celebrate = useCelebrate(action, first);

  const submitOne = async (values: V) => {
    try {
      const result = await action.submit(values, first);
      onOpenChange(false);
      onDone?.([result]);
      celebrate(values, { single: true, result });
    } catch (error) {
      const apiError = ApiError.from(error);
      if (apiError.isCanceled) return;
      const unmatched = applyFieldErrors(form, apiError.fieldErrors);
      setFailure({ feedback: apiError.feedback(), unmatched });
    }
  };

  const submitBatch = async (values: V, targets: readonly C[]) => {
    const results: R[] = [];
    const failed: { record: string; message: string }[] = [];
    for (const [index, target] of targets.entries()) {
      setProgress({ done: index, total: targets.length, failed, finished: false });
      try {
        results.push(await action.submit(values, target));
      } catch (error) {
        const apiError = ApiError.from(error);
        failed.push({ record: action.record(target), message: apiError.feedback().description || apiError.code });
      }
    }
    if (results.length > 0) onDone?.(results);
    if (failed.length === 0) {
      onOpenChange(false);
      celebrate(values, { single: false, count: results.length });
      return;
    }
    if (results.length > 0) celebrate(values, { single: false, count: results.length });
    setProgress({ done: targets.length, total: targets.length, failed, finished: true });
  };

  const submit = form.handleSubmit(async (values) => {
    setFailure(undefined);
    setBusy(true);
    try {
      if (batch) await submitBatch(values, batch);
      else await submitOne(values);
    } finally {
      setBusy(false);
    }
  });

  const finished = progress?.finished === true;

  const header = (
    <Header className="flex-row items-start gap-3.5">
      {Icon ? (
        <span
          aria-hidden
          className={cn('inline-flex size-10 shrink-0 items-center justify-center rounded-[10px]', destructive ? 'bg-err-wash text-err' : 'bg-accent-wash text-accent-text')}
        >
          <Icon className="size-5" />
        </span>
      ) : null}
      <div className="flex min-w-0 flex-col gap-1.5">
        <Title>{title}</Title>
        <Description>{summary}</Description>
      </div>
    </Header>
  );

  return (
    <FormProvider {...form}>
      <form
        noValidate
        className="flex min-h-0 flex-1 flex-col gap-4"
        onSubmit={(event) => {
          // O diálogo mora num portal, mas o evento do React sobe pela árvore até o <form> de quem o abriu.
          event.stopPropagation();
          void submit(event);
        }}
      >
        {header}
        <Body>
          {blocked ? <Alert tone="info">{resolvePatternText(i18n, blocked)}</Alert> : null}
          {failure ? <FailureAlert failure={failure} /> : null}
          {progress ? (
            <div role="status" aria-live="polite" className="flex flex-col gap-2">
              <p className="text-sm text-muted-foreground">
                {finished
                  ? t`${progress.failed.length} de ${progress.total} não foram concluídas.`
                  : t`Enviando ${progress.done + 1} de ${progress.total}…`}
              </p>
              {progress.failed.length > 0 ? (
                <ul className="flex flex-col gap-1 text-sm">
                  {progress.failed.map((item) => (
                    <li key={item.record} className="text-err">
                      <span className="font-mono">{item.record}</span> · {item.message}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
          {Fields && !finished ? (
            <fieldset disabled={busy} className="min-w-0">
              <FieldGrid>
                <Fields ctx={first} />
              </FieldGrid>
            </fieldset>
          ) : null}
        </Body>
        <Footer>
          {finished ? (
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              {t`Fechar`}
            </Button>
          ) : (
            <>
              <Button type="button" variant="secondary" disabled={busy} onClick={() => onOpenChange(false)}>
                {t`Não, cancelar`}
              </Button>
              <Button type="submit" variant={destructive ? 'danger-solid' : 'primary'} loading={busy} disabled={blocked !== undefined}>
                {t`Sim, ${verb}`}
              </Button>
            </>
          )}
        </Footer>
      </form>
    </FormProvider>
  );
}

interface StepUpActionSheetProps<C, V extends FieldValues, R> extends ActionDialogProps<C, V, R> {
  readonly stepUp: StepUpSubmit<C, V, R>;
}

/**
 * A ação com step-up no Sheet único de transação (W11): com formulário, Dados → Revisão → Código; sem formulário, a
 * prévia e o código na mesma etapa (Revisão concluída no trilho). "Voltar" preserva o que foi digitado.
 */
function StepUpActionSheet<C, V extends FieldValues, R>({ action, ctx, batch, open, onOpenChange, onDone, stepUp }: StepUpActionSheetProps<C, V, R>) {
  // A prévia lê o rascunho que os `Fields` escrevem (`ReviewStore`) e os `Fields` leem o `formState` pelo contexto: nada
  // aqui pode ser memorizado pelo React Compiler.
  'use no memo';
  const { i18n, t } = useLingui();
  const formId = useId();
  const items: readonly C[] = batch ?? (ctx === undefined ? [] : [ctx]);
  const first = items[0] as C;
  const form = useActionForm(action, first);
  const fieldsStep = useActionStepHost();
  const celebrate = useCelebrate(action, first);
  const [failure, setFailure] = useState<Failure | undefined>(undefined);
  const [values, setValues] = useState<V | undefined>(undefined);
  const Fields = action.Fields;
  // O legado nunca manda N operações com um código só (SPEC, Lote).
  const blocked = action.blocked?.(first) ?? (batch ? msg`Esta ação pede senha e código MFA e não roda em lote.` : undefined);
  const builtInReview = Boolean(Fields) && stepUp.review !== 'fields';
  const [step, setStep] = useState<TransactionStep>(Fields ? 'data' : blocked ? 'review' : 'code');
  // O caminho sem Revisão (`useActionStep({ review: false })`) vale até o código, quando os `Fields` já saíram.
  const [skipReview, setSkipReview] = useState(false);
  const withoutReview = step === 'data' ? fieldsStep.noReview : skipReview;
  const steps: readonly TransactionStep[] = Fields ? (withoutReview ? ['data', 'code'] : ['data', 'review', 'code']) : ['review', 'code'];

  const title = resolvePatternText(i18n, action.title(first));
  const summary = resolvePatternText(i18n, batch && action.batchSummary ? action.batchSummary(batch) : action.summary(first));
  const verb = resolvePatternText(i18n, action.verb).toLocaleLowerCase(i18n.locale);
  const texts = stepUp.texts;
  const current = values ?? form.getValues();
  const rows = typeof stepUp.review === 'function' && step === 'review' ? stepUp.review(current, first, i18n) : [];

  const advance = form.handleSubmit((valid) => {
    setFailure(undefined);
    setValues(valid);
    setSkipReview(fieldsStep.noReview);
    setStep(builtInReview && !fieldsStep.noReview ? 'review' : 'code');
  });

  const notice = (
    <>
      {blocked ? <Alert tone="info">{resolvePatternText(i18n, blocked)}</Alert> : null}
      {failure ? <FailureAlert failure={failure} /> : null}
    </>
  );

  const dataPrimary = fieldsStep.primary
    ? fieldsStep.primary.onSelect
      ? fieldsStep.primary
      : { ...fieldsStep.primary, form: formId }
    : { label: t`Continuar`, form: formId };

  return (
    <TransactionSheet<R>
      open={open}
      onOpenChange={onOpenChange}
      slot="action-dialog"
      title={title}
      {...(action.icon ? { icon: action.icon } : {})}
      destructive={action.kind === 'destructive'}
      steps={steps}
      step={step}
      {...(step === 'data' && fieldsStep.step ? { trailStep: fieldsStep.step } : {})}
      description={summary}
      notice={notice}
      primary={
        step === 'data'
          ? { ...dataPrimary, disabled: blocked !== undefined || dataPrimary.disabled === true }
          : { label: t`Continuar`, onSelect: () => setStep('code'), disabled: blocked !== undefined }
      }
      {...(step === 'data' ? (fieldsStep.back ? { onBack: fieldsStep.back } : {}) : Fields ? { onBack: () => setStep('data') } : {})}
      confirm={stepUp.grant ? stepUp.grant(current, first) : (proof) => stepUp.submit(current, first, proof)}
      onDone={(result) => {
        onOpenChange(false);
        onDone?.([result]);
        celebrate(current, { single: true, result });
      }}
      {...(Fields ? { onCodeBack: () => setStep(builtInReview && !skipReview ? 'review' : 'data') } : {})}
      submitLabel={texts.submitLabel ? resolvePatternText(i18n, texts.submitLabel) : t`Sim, ${verb}`}
      onOperationError={(error) => {
        if (!Fields) return false;
        const unmatched = applyFieldErrors(form, error.fieldErrors);
        if (unmatched.length === error.fieldErrors.length) return false;
        setFailure({ feedback: error.feedback(), unmatched });
        setStep('data');
        return true;
      }}
    >
      {step === 'data' && Fields ? (
        <FormProvider {...form}>
          <ActionStepProvider host={fieldsStep.host}>
            <form
              id={formId}
              noValidate
              className="flex min-w-0 flex-col gap-4"
              onSubmit={(event) => {
                // O Sheet mora num portal, mas o evento do React sobe pela árvore até o <form> de quem o abriu.
                event.stopPropagation();
                void advance(event);
              }}
            >
              <FieldGrid>
                <Fields ctx={first} />
              </FieldGrid>
            </form>
          </ActionStepProvider>
        </FormProvider>
      ) : null}
      {step === 'review' ? <ReviewRows rows={rows} /> : null}
    </TransactionSheet>
  );
}
